import {
  FileText,
  CheckCircle2,
  Clock,
  XCircle,
  MinusCircle,
  ExternalLink,
  FolderOpen,
  FileSignature,
} from 'lucide-react';
import {
  useApprenantDocuments,
  type DocItem,
  type DocStatus,
} from '../hooks/useApprenantDocuments.js';
import { CollapsibleSlice } from '../../../components/CollapsibleSlice.js';

interface Props {
  apprenantId: string;
}

/**
 * Slice DocumentsCard — domaine Documents (Référentiel + Drive)
 *
 * KPI principal (toujours visible) :
 *   - X / Y documents signés
 *   - X feuilles d'émargement / Y heures émargées (cf. demande Rafik iter 3)
 *
 * Détail collapsible : liste docs + lien Drive + signatures.
 */
export function DocumentsCard({ apprenantId }: Props) {
  const { data: d, isLoading, error } = useApprenantDocuments(apprenantId);

  if (isLoading || error || !d) {
    return (
      <CollapsibleSlice title="Documents" icon={<FileText className="text-primary-500 h-4 w-4" />}>
        <div className="text-sm text-neutral-500">{error?.message ?? 'Chargement...'}</div>
      </CollapsibleSlice>
    );
  }

  const docsSigned = d.docs.filter((doc) => doc.status === 'signed').length;
  const docsTotal = d.docs.filter((doc) => doc.status !== 'not_applicable').length;
  const emargPct = Math.round((d.emargement.signedHours / d.emargement.expectedHours) * 100);

  return (
    <CollapsibleSlice
      title="Documents"
      icon={<FileText className="text-primary-500 h-4 w-4" />}
      badge={
        d.emargement.driveFolderUrl ? (
          <a
            href={d.emargement.driveFolderUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-primary-50 text-primary-700 hover:bg-primary-100 flex items-center gap-1 rounded px-2 py-0.5 text-[10px] font-semibold"
          >
            <FolderOpen className="h-3 w-3" />
            Drive
          </a>
        ) : null
      }
      summary={
        <div className="grid grid-cols-2 gap-2">
          {/* Documents signés */}
          <div className="rounded-md border border-neutral-200 bg-neutral-50 p-2">
            <div className="flex items-center gap-1 text-[9px] uppercase tracking-wider text-neutral-500">
              <FileText className="h-3 w-3" />
              Documents
            </div>
            <div className="mt-0.5 text-sm">
              <span className="text-base font-bold text-neutral-900">{docsSigned}</span>
              <span className="text-neutral-400"> / {docsTotal} signés</span>
            </div>
            <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-white">
              <div
                className="h-full rounded-full bg-emerald-500"
                style={{ width: `${docsTotal > 0 ? (docsSigned / docsTotal) * 100 : 0}%` }}
              />
            </div>
          </div>

          {/* Émargements — KPI critique */}
          <div className="border-primary-200 bg-primary-50 rounded-md border p-2">
            <div className="text-primary-700 flex items-center gap-1 text-[9px] uppercase tracking-wider">
              <FileSignature className="h-3 w-3" />
              Émargements
            </div>
            <div className="mt-0.5 text-sm">
              <span className="text-primary-900 text-base font-bold">
                {d.emargement.signedCount}
              </span>
              <span className="text-primary-700"> feuilles · </span>
              <span className="text-primary-900 text-base font-bold">
                {d.emargement.signedHours}h
              </span>
              <span className="text-primary-700"> / {d.emargement.expectedHours}h</span>
            </div>
            <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-white">
              <div
                className="bg-primary-600 h-full rounded-full"
                style={{ width: `${emargPct}%` }}
              />
            </div>
          </div>
        </div>
      }
    >
      {/* Liste des documents */}
      <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-neutral-500">
        Documents officiels
      </div>
      <ul className="space-y-1">
        {d.docs.map((doc, i) => (
          <DocRow key={i} doc={doc} />
        ))}
      </ul>

      {/* Récap émargement détaillé */}
      <div className="mt-3 rounded-lg border border-neutral-200 bg-neutral-50 p-2.5">
        <div className="flex items-baseline justify-between">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-neutral-700">
            Feuilles d'émargement
          </span>
          <span className="text-[10px] text-neutral-500">
            {d.emargement.signedCount}/{d.emargement.expectedCount} feuilles ·{' '}
            {d.emargement.signedHours}h/{d.emargement.expectedHours}h signées
          </span>
        </div>
        <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-white">
          <div className="bg-primary-500 h-full rounded-full" style={{ width: `${emargPct}%` }} />
        </div>
        {d.emargement.driveFolderUrl && (
          <a
            href={d.emargement.driveFolderUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary-600 mt-2 flex items-center gap-1 text-[10px] font-medium hover:underline"
          >
            <FolderOpen className="h-3 w-3" />
            Voir le dossier Drive
            <ExternalLink className="h-2.5 w-2.5" />
          </a>
        )}
      </div>
    </CollapsibleSlice>
  );
}

// ────────────────────────────────────────────────────────────────────

function DocRow({ doc }: { doc: DocItem }) {
  return (
    <li className="flex items-center gap-2 rounded px-2 py-1.5 text-xs hover:bg-neutral-50">
      <DocStatusIcon status={doc.status} />
      <span className="flex-1">
        <span className="font-medium text-neutral-800">{doc.label}</span>
        {doc.signedAt && (
          <span className="ml-2 text-[10px] text-neutral-500">
            signé le {new Date(doc.signedAt).toLocaleDateString('fr-FR')}
          </span>
        )}
        {doc.status === 'pending' && (
          <span className="ml-2 text-[10px] italic text-amber-700">en attente</span>
        )}
      </span>
      {doc.driveUrl ? (
        <a
          href={doc.driveUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="bg-primary-50 text-primary-700 hover:bg-primary-100 flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium"
        >
          Drive
          <ExternalLink className="h-2.5 w-2.5" />
        </a>
      ) : (
        <span className="text-[10px] text-neutral-300">—</span>
      )}
    </li>
  );
}

function DocStatusIcon({ status }: { status: DocStatus }) {
  switch (status) {
    case 'signed':
      return <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />;
    case 'pending':
      return <Clock className="h-3.5 w-3.5 text-amber-500" />;
    case 'missing':
      return <XCircle className="h-3.5 w-3.5 text-rose-500" />;
    case 'not_applicable':
      return <MinusCircle className="h-3.5 w-3.5 text-neutral-300" />;
  }
}
